/**
 * Popover Component
 */
import React, { useCallback, useState } from 'react';
import ReactDOM from 'react-dom';
import { withTheme } from 'styled-components';
import { PopoverProps, ShowOnEvent } from './popover.types';
import PopperComponent from './popperComponent';
import { ReferenceWrapper, PopperContainer } from './popover.style';
import useControlledState from '../hooks/useControlledState/useControlledState';
import ScrollLock from '../ScrollLock/scrollLock';
import ScrollbarWidthOffset from '../ScrollbarWidthOffset/scrollbarWidthOffset';
import Backdrop from '../Modal/Backdrop/backdrop';

const DEFAULT_DATA_ATTR = { 'data-testid': 'popover__backdrop' };

const Popover = (props: PopoverProps) => {

  const {
    showMultiple = false,
    showOnEvent = ShowOnEvent.CLICK,
    children,
    portalTarget,
    onOpen,
    onClose,
    closeOnPopperClick = true,
    isOpen,
    defaultIsOpen = false,
    preventOverflow = true,
    overflowOverReference = false,
    flipPopover = true,
    showArrow = true,
    BackdropComponent = Backdrop,
    backdropProps,
    enableBackdropClick = false,
    enableScrollbarWidthOffset = false,
    enableScrollLock = false,
    enableBackdrop = false,
    popoverOffset,
    preventOverflowPadding,
    placement,
    popoverContent,
    theme,
    className,
  } = props;

  const [isPopoverOpen, setIsPopoverOpen] = useControlledState({
    controlled: isOpen,
    default: defaultIsOpen,
    name: 'Popover',
  });
  const [refElement, setRefElement] = useState<HTMLElement|null>(null);

  const openPopover = useCallback(() => {
    setIsPopoverOpen(true);
    if(onOpen) {
      onOpen();
    }
  }, [setIsPopoverOpen, onOpen]);
  const closePopover = useCallback(() => {
    setIsPopoverOpen(false);
    if(onClose) {
      onClose();
    }
  }, [setIsPopoverOpen, onClose]);
  const clickOutsidePopperHandler = useCallback(() => {

    const couldClose = !showMultiple && isPopoverOpen;
    if (couldClose) {

      closePopover();

    }

  }, [showMultiple, isPopoverOpen, closePopover]);
  const togglePopover = useCallback(() => {
    if(isPopoverOpen) {
      closePopover();
    } else {
      openPopover();
    }
  }, [isPopoverOpen, closePopover, openPopover]);

  const clickHandlers = {
    onClick: () => togglePopover(),
  };
  const hoverHandlers = {
    onMouseEnter: openPopover,
    onMouseLeave: closePopover,
  };
  const onBackdropClick = useCallback(() => {
    if (enableBackdropClick) {
      closePopover();
    }
  },[enableBackdropClick, closePopover]);
  const hasDocument = typeof document !== undefined;
  const isShowingBackdrop = isPopoverOpen && enableBackdrop;
  const popper = (
    <PopperContainer isShowingBackdrop={isShowingBackdrop}>
      {enableScrollbarWidthOffset && <ScrollbarWidthOffset /> }
      {enableScrollLock && <ScrollLock /> }
      {enableBackdrop && <BackdropComponent dataAttr={DEFAULT_DATA_ATTR} onClick={onBackdropClick} {...backdropProps} /> }
      <PopperComponent
        referenceElement={refElement}
        clickOutsidePopperHandler={clickOutsidePopperHandler}
        popperClickHandler={closeOnPopperClick ? closePopover : undefined}
        preventOverflow={preventOverflow}
        overflowOverReference={overflowOverReference}
        flipPopover={flipPopover}
        showArrow={showArrow}
        popoverOffset={popoverOffset}
        preventOverflowPadding={preventOverflowPadding}
        placement={placement}
        popoverContent={popoverContent}
        theme={theme}
        className={className}
      />
    </PopperContainer>
  );
  const popperElement = portalTarget && hasDocument ?
    ReactDOM.createPortal(popper, portalTarget)
    : popper;

  return (<>
    <ReferenceWrapper
      {...(showOnEvent === ShowOnEvent.CLICK ? clickHandlers : hoverHandlers)}
      ref={setRefElement}
      isShowingBackdrop={isShowingBackdrop}
    >
      {children}
    </ReferenceWrapper>
    {isPopoverOpen ? (
      popperElement
    ) : null}
  </>);
};

export default withTheme(Popover);
